import { memoize } from 'underscore'

import paymentMethodsConf from '@/configuration/sources/smartform/paymentMethodsConf.yml'
import Events from '@/configuration/Events'
import { initSmartformMethods } from '@/store/utils/smartform'
import { urlToSvg, ensureUniqId } from '@/common/util/svg'
import Modal from '@/configuration/sources/SimpleModal.yml'
import { MethodDescriptor } from '@/common/model/PaymentMethod'
import { isSmartFormForcedTokenHelper } from '@/host/service/DNA/Helper/isSmartFormForcedTokenHelper.js'
import {
  CoBrands,
  SubBrands,
  notAllowedInKrEmbedded
} from '@/configuration/sources/Brands.yml'
import { getLayoutState, layoutGetters } from '@/store/modules/layout'
import { isValidUrl } from '@/common/util/url'

import {
  getSplitPaymentState,
  getSplitPaymentActions,
  getSplitPaymentGetters
} from '@/store/modules/smartform/splitPayment'
import {
  getNestedMethodState,
  nestedMethodActions,
  nestedMethodGetters
} from '@/store/modules/smartform/nestedMethod'
import {
  getRetrySupportState,
  retrySupportActions,
  retrySupportGetters
} from '@/store/modules/smartform/retrySupport'

export const getSmartFormState = () => {
  return {
    smartButton: {
      forceSkeleton: false, // @since KJS-3907 | For test
      nativeLook: { paymentMethods: [] }
    },
    smartForm: {
      ...getLayoutState(),
      ...getSplitPaymentState(),
      ...getNestedMethodState(),
      ...getRetrySupportState(),
      type: 'embedded', // popin
      size: 'M', // XS, S, M, L, XL
      displayOptions: {
        cardsIntegrated: true, // only used to read the html attribute (use the cardsFormExpanded getter instead)
        cardHeader: true
      },
      labels: {
        otherPaymentMethods: null
      },
      defaultMethod: '',
      selectedMethod: null,
      activeMethod: null,
      activeMethodMetadata: null,
      saveMethod: {}, // [METHOD]: boolean
      deadEndPaymentMethod: false,
      forceRedirectionType: null,
      forceSkeleton: false, // @since KJS-3907 | For test
      methodDisplayMode: 'MINIMAL',
      outsideCardsForm: false,
      hidden: false,
      isOpen: false,
      paymentDone: false,
      // Methods
      paymentMethods: initSmartformMethods(),
      availablePaymentMethods: [],
      dnaPaymentMethods: [],
      userPaymentMethodsOrder: [],
      allowedPaymentMethods: ['all'],
      preferredPaymentMethods: null,
      dnaPreferredPaymentMethods: [],
      excludedPaymentMethods: [],
      // Brands
      dnaCardBrands: [],
      // Smart Buttons
      smartButtons: [],
      // Navigation
      navigation: {
        views: ['full', 'group', 'content'], // full, group, content
        currentView: '',
        history: []
      },
      // Grouping
      groupingThreshold: 7,
      activeGroup: null,
      activeGroupSection: null, // ppm | opm
      singlePaymentButton: false,
      wallet: {
        layout: 'inherit', // inherit | compact | column
        displayCardholderName: false
      },
      nativeLook: {
        enabled: {
          preferredMethods: true,
          otherMethods: false
        },
        paymentMethods: [] // [] = all (if enabled=true)
      }
    }
  }
}

export const smartFormMutations = {}

export const smartFormActions = ($locator, app) => {
  return {
    ...getSplitPaymentActions(),
    ...nestedMethodActions,
    ...retrySupportActions,
    /**
     * Single Payment Button Mode
     * Open previously selected payment method.
     * Raise error if widget is not smartform.
     * Raise warning if no payment method has been selected.
     *
     * @since KJS-2614
     */
    openSelectedMethod({ state, dispatch, getters }) {
      const { isCardBrandAsMethod, isSmartForm, isFallbackSmartButtonVisible } =
        getters
      let { selectedMethod } = state.smartForm

      dispatch('cleanError')

      if (!isSmartForm) return dispatch('error', 'CLIENT_509')

      // If using the smartButton fallback with only 1 payment method select it by default
      if (isFallbackSmartButtonVisible) {
        const { effectivePaymentMethodList } = getters
        dispatch('selectMethod', effectivePaymentMethodList[0])
      }

      selectedMethod = state.smartForm.selectedMethod
      if (!selectedMethod) {
        $locator.$bus.$emit(Events.krypton.smartform.warningFlash)
        return dispatch('warning', {
          errorCode: 'CLIENT_601',
          metadata: {
            showInForm: true
          }
        })
      }
      // Check first if form card is expanded and user started to fill it
      // or if the selected method is an embedded card form
      if (
        (selectedMethod === 'CARDS' && getters.cardsFormExpanded) ||
        isCardBrandAsMethod(selectedMethod)
      ) {
        $locator.$bus.$emit(Events.krypton.form.pay, {
          formId: getters.activeFormId
        })
        return
      }
      dispatch('openMethod', selectedMethod)
    },
    /**
     * Single Payment Button Mode
     * Unselect previously selected method.
     *
     * @since KJS-2614
     */
    unselectMethod({ state, commit }) {
      const { smartForm } = state
      if (smartForm.selectedMethod) {
        commit('UPDATE', {
          smartForm: {
            selectedMethod: null
          }
        })
      }
    },
    /**
     * Single Payment Button Mode
     * Select method without opening it yet.
     *
     * @since KJS-2614
     */
    async selectMethod({ state, commit, dispatch, getters }, selectedMethod) {
      dispatch('cleanError')

      if (getters.isNestedMethod(selectedMethod) || getters.cardsFormExpanded) {
        const method = MethodDescriptor.create(selectedMethod)
        const isCardFormMethod =
          selectedMethod.startsWith('CARDS') &&
          !getters.hasToken(method.metadata)

        commit('UPDATE', {
          smartForm: {
            activeMethod: isCardFormMethod ? method.name : null,
            activeMethodMetadata: isCardFormMethod ? method.metadata : null
          },
          activeForm: getters.cardFormInsideButton(selectedMethod)
            ? selectedMethod
            : 'default'
        })
      } else dispatch('setDefaultActiveMethod')

      if (state.smartForm.selectedMethod !== selectedMethod) {
        $locator.$bus.$emit(
          Events.krypton.smartform.paymentMethodSelected,
          selectedMethod
        )
        commit('UPDATE', {
          smartForm: {
            selectedMethod
          }
        })
      }
    },
    closeMethod({ state, dispatch, getters }, backNavigation = false) {
      const method = state.smartForm.activeMethod
      const { formMode } = state

      // Skip the action if the method is already closed
      if (getters.isDefaultActiveMethod) return

      if (method === 'CARDS' && formMode === 'wallet') {
        const formId = getters.cardsFormExpanded
          ? state.forms.modal
          : state.forms.default
        dispatch(`cardForm_${formId}/closeWalletPayment`)
      } else if (method === 'APPLE_PAY') dispatch('closeApplePay')

      // Reset the method
      dispatch('finishRedirection')
      dispatch('setDefaultActiveMethod')
      // For window redirection, we check the transaction separately
      if (method && getters.getRedirectionType(method) !== 'iframe')
        dispatch('checkTransaction')

      const {
        isExtrasFormVisible,
        isSmartFormPopin,
        isGroupActive,
        isPaymentDone
      } = getters

      dispatch('setNavigableElements')
      // Extras form - Manage separately the navigation
      if (isExtrasFormVisible && !backNavigation) return

      const { isNavigationOnContent } = getters
      if (isNavigationOnContent || backNavigation) dispatch('navigateBack')

      if ((!isSmartFormPopin && !isGroupActive) || isPaymentDone) {
        dispatch('update', { smartForm: { isOpen: false } })
      }
      if (state.activeForm !== 'default') {
        dispatch('focusForm', 'default')
      }
      // Clear the nested method in the toolbar
      $locator.$bus.$emit(Events.krypton.toolbar.clearNestedMethodTestData)
    },
    openMethod({ dispatch, commit, getters, state }, method) {
      if (!method) return
      method = MethodDescriptor.create(method)

      const activeMethod = method.name
      const metadata = method.metadata

      dispatch('cleanError')

      // Demo token - raise error
      // In popin mode with cards, allow the flow to continue to show the form
      const isPopinCardFlow =
        getters.isSmartFormPopin && getters.isCardMethod(activeMethod)
      if (getters.isDemoToken && !isPopinCardFlow) {
        dispatch('error', {
          errorCode: 'CLIENT_998',
          paymentMethod: activeMethod
        })
        return
      }

      // Always reset nestedMethod testData when opening a new method
      if (state.smartForm.nestedMethod.testData)
        dispatch('setNestedMethodTestData', null)

      // Nested methods
      if (getters.isNestedMethod(method) && app !== 'ghost') {
        // If click on same method close it
        if (
          state.smartForm.activeMethod === activeMethod &&
          state.smartForm.activeMethodMetadata === metadata
        )
          dispatch('setDefaultActiveMethod')
        else {
          commit('UPDATE', {
            smartForm: {
              activeMethod,
              activeMethodMetadata: metadata
            }
          })
        }

        return
      }

      // Wallet Payment
      if (getters.hasToken(metadata) && getters.isCardMethod(activeMethod)) {
        dispatch(`startWalletPayment`, {
          method: activeMethod,
          paymentMethodToken: metadata
        })
        // since KJS-4189 don't show the form if no extra info is needed and launch the payment directly
        // Continue the flow only for cards if extra info is needed
        if (
          !getters.hasCardToken(metadata) ||
          getters.hasCardTokenAndNoExtraInfoIsNeeded(metadata)
        )
          return
      }

      // Cards Form Popin fallback to open the popin
      if (getters.isCardsFormPopin && getters.isCardMethod(activeMethod)) {
        dispatch('openPopin')
        return
      }
      const redirectionType = getters.getRedirectionType(activeMethod)
      const isMethodAvailableInDna =
        getters.isMethodAvailableInDna(activeMethod)

      // Method not available in the DNA - raise error
      if (!isMethodAvailableInDna) {
        // Active Payment method not available
        dispatch('error', 'CLIENT_503')
        return
      }

      if (app === 'ghost' || window.redirectionWindow) {
        commit('UPDATE', {
          smartForm: {
            activeMethod,
            activeMethodMetadata: metadata ?? null,
            deadEndPaymentMethod:
              paymentMethodsConf.deadEndPaymentMethod.includes(activeMethod) ||
              !!state.dna.smartForm?.[activeMethod]?.deadEndPaymentMethod,
            methodDisplayMode:
              paymentMethodsConf.displayMode[activeMethod] ||
              getSmartFormState().smartForm.methodDisplayMode
          },
          redirectionType
        })
        if (redirectionType === 'iframe') {
          if (!state.smartForm.isOpen) dispatch('openSmartFormPopin')
          dispatch('setNavigableElements')
          dispatch('navigate', 'content')
        }
        return
      }

      // Check retry support for the payment method
      if (getters.shouldPreventRetry(activeMethod)) {
        dispatch('error', {
          errorCode: 'CLIENT_107',
          paymentMethod: activeMethod
        })
        return
      }

      // Mark payment method as attempted
      dispatch('addPaymentMethodAttempt', activeMethod)

      // Reset activeForm, when selecting another payment method the
      // card forms inside buttons like illicado have to collapse
      if (state.activeForm !== 'default') {
        commit('UPDATE', {
          activeForm: 'default'
        })
      }

      if (activeMethod === 'SAMSUNG_PAY') {
        commit('UPDATE', {
          smartForm: { activeMethod: 'SAMSUNG_PAY', activeMethodMetadata: null }
        })
        if (
          getters.isMobile ||
          getters.isTablet ||
          getters.isSamsungPaySimulator
        ) {
          $locator.proxy.send(
            $locator.storeFactory.create('launchSamsungPayPayment')
          )
        } else {
          //showing ghost before starting
          const redirectStore = $locator.storeFactory.create(
            'billingRedirect',
            {}
          )
          if (!state.smartForm.isOpen)
            dispatch('openSmartFormPopin', { withHistory: false })
          dispatch('startRedirection', redirectStore)
          if (!getters.isGridMode) dispatch('navigate', 'content')
        }
      } else if (activeMethod === 'GOOGLEPAY') {
        $locator.proxy.send(
          $locator.storeFactory.create('startGooglePay'),
          false,
          'payment',
          true,
          'GOOGLEPAY'
        )
      } else if (activeMethod === 'APPLE_PAY') {
        // Apple Pay
        dispatch('startApplePayPayment')
      } else if (getters.shouldOpenCardFormPopin(method)) {
        // Card method
        commit('UPDATE', {
          smartForm: {
            activeMethod,
            activeMethodMetadata: metadata ?? null,
            deadEndPaymentMethod: false
          }
        })
        if (getters.isCardBrandAsMethod(method)) {
          dispatch(`cardForm_${state.forms.default}/update`, {
            forcedBrand: metadata
          })
        } else if (!getters.hasToken(metadata) && !state.dna.tokens?.forced) {
          // Need to clear brand because in popin mode CARDS & ILLICADO share
          // the same card actual card form
          // @KJS-5135 exclude wallet card as the brand has already been set
          // above in the same action
          // @KJS-5181 also exclude forced token case, as the brand is already set too
          dispatch(`cardForm_${state.forms.default}/resetBrand`)
        }
        const view =
          !!metadata && (getters.cardsFormExpanded || getters.isFormPopin)
            ? 'extra'
            : 'content'
        const withoutHistory = getters.hasSmartButton(activeMethod)
        if (!getters.isAnyPopinOpen)
          dispatch('openSmartFormPopin', { withHistory: !withoutHistory })
        dispatch('setNavigableElements')
        dispatch('navigate', view)
        if (view === 'extra') {
          dispatch('focusForm', 'modal')
        }
        // For smartform popin mode + [cards:method] method set the test data in the toolbar
        if (!!metadata && getters.isSmartFormPopin) {
          $locator.$bus.$emit(Events.krypton.toolbar.setNestedMethodTestData, {
            method: metadata
          })
        }
      } else dispatch('openRedirectionMethod', method)
    },
    openRedirectionMethod({ commit, getters, state, dispatch }, method) {
      if (!method) return
      method = MethodDescriptor.create(method)

      const activeMethod = method.name
      const metadata = method.metadata

      const redirectionType = getters.getRedirectionType(activeMethod)

      if (redirectionType === 'iframe') {
        commit('UPDATE', {
          smartForm: {
            activeMethod
          }
        })
        $locator.proxy.send(
          $locator.storeFactory.create('redirect', {
            paymentMethod: activeMethod
          })
        )
      } else if (~['popup', 'tab'].indexOf(redirectionType)) {
        if (
          activeMethod.startsWith('PAYPAL') &&
          !getters.isRegisterMode &&
          (state.dna.formAction === 'CUSTOMER_WALLET' ||
            state.dna.formAction === 'ASK_REGISTER_PAY') &&
          state.doRegister.force !== true &&
          getters.canRegisterMethod(activeMethod) &&
          !getters.hasToken(metadata)
        ) {
          const layouts = Modal.layouts
          dispatch('openModal', {
            layout: layouts.PAYPAL.registerMethod,
            method: activeMethod
          })
        } else {
          dispatch('showWindow', activeMethod)
        }
      }
    },
    setSaveMethod({ commit }, { method }) {
      commit('UPDATE', { smartForm: { saveMethod: { [method]: true } } })
    },
    unsetSaveMethod({ commit }, { method }) {
      commit('UPDATE', { smartForm: { saveMethod: { [method]: false } } })
    },
    openGroup(
      { commit, getters, dispatch },
      { activeGroup, activeGroupSection }
    ) {
      commit('UPDATE', {
        smartForm: {
          activeGroup,
          ...(getters.hasPreferredPaymentMethods && { activeGroupSection })
        }
      })
      dispatch('setDefaultActiveMethod')
      dispatch('cleanError')
      dispatch('openSmartFormPopin')
    },
    closeGroup({ dispatch, commit }) {
      dispatch('closeMethod')
      commit('UPDATE', {
        smartForm: { activeGroup: null, activeGroupSection: null }
      })
      dispatch('setDefaultActiveMethod')
    },
    setPaymentMethods(
      { commit, dispatch },
      { paymentMethods, preferredMethods }
    ) {
      const defaultMethod = paymentMethods[0] ? paymentMethods[0] : 'CARDS'

      commit('UPDATE', {
        smartForm: {
          availablePaymentMethods: paymentMethods,
          dnaPaymentMethods: paymentMethods,
          dnaPreferredPaymentMethods: preferredMethods,
          defaultMethod
        }
      })
      if (paymentMethods.includes('APPLE_PAY'))
        $locator.$bus.$once(Events.krypton.dna.parsed, () =>
          dispatch('applePayMode')
        )
      if (paymentMethods.includes('GOOGLEPAY'))
        $locator.$bus.$once(Events.krypton.dna.parsed, () =>
          dispatch('setupGooglePayHost')
        )
      if (paymentMethods.includes('SAMSUNG_PAY'))
        $locator.$bus.$once(Events.krypton.dna.parsed, () =>
          dispatch('setupSamsungPayHost')
        )
    },
    setAvailablePaymentMethods({ commit, dispatch }, paymentMethods) {
      commit('UPDATE', {
        smartForm: {
          availablePaymentMethods: paymentMethods
        }
      })
    },
    removePaymentMethod({ commit, state }, paymentMethod) {
      commit('UPDATE', {
        smartForm: {
          availablePaymentMethods:
            state.smartForm.availablePaymentMethods.filter(
              method => method !== paymentMethod
            )
        }
      })

      if (paymentMethod === 'CARDS') return
    },
    removeDnaPaymentMethod({ commit, state }, paymentMethod) {
      commit('UPDATE', {
        smartForm: {
          dnaPaymentMethods: state.smartForm.dnaPaymentMethods.filter(
            method => method !== paymentMethod
          )
        }
      })
    },
    paymentProcessed({ commit, dispatch }) {
      commit('UPDATE', {
        smartForm: {
          paymentDone: true
        }
      })
      dispatch('disableForm')
    },
    closeSmartFormPopin({ state, commit, dispatch, getters }) {
      // Skip it in case the popin is not open (can be embedded and popin mode)
      if (!state.smartForm.isOpen) return
      if (!getters.isDeadEndPaymentMethod && !getters.isAbortedError)
        dispatch('cleanError')

      // Except when the 3ds is open, we clear all the fields
      const formId = state.forms[state.activeForm]
      if (!state.processingPayment)
        dispatch(`cardForm_${formId}/forceFieldClear`)

      // Close modal
      commit('UPDATE', { smartForm: { isOpen: false } })
      if (app === 'host') {
        const $ghost = $locator.iframeController.ghostContainer
        $ghost.hide()
      }
      dispatch('closeGroup')
      dispatch('runCallback', 'onPopinClosed')
    },
    openSmartFormPopin({ commit, state, dispatch, getters }, options = {}) {
      const { withHistory = true } = options
      if (withHistory)
        dispatch('navigate', state.smartForm.activeGroup ? 'group' : 'full')
      commit('UPDATE', { smartForm: { isOpen: true } })
    },
    openSmartFormPopinWrapper({ dispatch, getters }) {
      if (getters.popinOpensMethodDirectly)
        dispatch('openMethod', getters.effectivePaymentMethodList[0])
      else dispatch('openSmartFormPopin')
    },
    setCardBrands({ commit }, brands) {
      brands = brands.filter(brand => {
        return brand !== 'DEFAULT'
      })
      commit('UPDATE', {
        smartForm: { dnaCardBrands: brands }
      })
    },
    navigate({ commit, state }, view) {
      const { history, currentView } = state.smartForm.navigation
      if (view !== currentView) {
        history.push(view)
        commit('UPDATE', {
          smartForm: {
            navigation: { currentView: view, history }
          }
        })
      }
    },
    navigateBack({ commit, state, dispatch, getters }) {
      const { isGridMode } = getters
      const { history: historyParam, currentView } = state.smartForm.navigation
      const history = [...historyParam]
      history.pop() // remove the last view

      if (history.length) {
        if (currentView === 'group' && !isGridMode) dispatch('closeGroup')
        commit('UPDATE', {
          smartForm: {
            navigation: {
              currentView: history[history.length - 1],
              history
            }
          }
        })
      } else {
        commit('UPDATE', {
          smartForm: {
            navigation: { history }
          }
        })
        // Do not close the popin in case of error (Apple Pay simulator)
        if (!getters.hasError) dispatch('closeSmartFormPopin')
      }
      // Finish the redirection - in case of
      dispatch('finishRedirection')
    },
    addSmartButton(
      { commit, state, dispatch },
      { paymentMethod, hasNativeLook }
    ) {
      const updatePayload = {
        smartForm: {
          smartButtons: [paymentMethod, ...state.smartForm.smartButtons]
        }
      }

      if (hasNativeLook)
        updatePayload.smartButton = {
          nativeLook: {
            paymentMethods: [
              paymentMethod,
              ...state.smartButton.nativeLook.paymentMethods
            ]
          }
        }

      commit('UPDATE', updatePayload)

      if (paymentMethod !== 'CARDS') {
        dispatch('removePaymentMethod', paymentMethod)
      }
    },
    displaySmartFormModal({ state, dispatch, getters }) {
      // In some cases SmartFormModal must be opened to display
      // SmartForm + expanded card form + 3DS:
      // Dcc planet iframe and receipt
      if (state.formWidget === 'smartForm' && !getters.isSmartFormOpen) {
        // without history to avoid to show the popin with the cards payment method loading
        // after 3ds in grid mode
        dispatch('openSmartFormPopin', { withHistory: false })
        dispatch('setNavigableElements')
        const redirectView = getters.isGridMode ? 'redirection' : 'content'
        dispatch('navigate', redirectView)
      }
    },
    setDefaultActiveMethod({ commit, state, dispatch, getters }) {
      commit('UPDATE', {
        activeForm: 'default',
        smartForm: {
          activeMethod: getters.defaultActiveMethod,
          activeMethodMetadata: null
        }
      })

      if (state.smartForm.nestedMethod.testData)
        dispatch('setNestedMethodTestData', null)
    }
  }
}
export const smartFormGetters = {
  ...getSplitPaymentGetters(),
  ...nestedMethodGetters,
  ...retrySupportGetters,
  ...layoutGetters,
  isSmartFormSmall: ({ smartForm }) => smartForm.size.substr(-1) === 'S',
  isSmartFormMedium: ({ smartForm }) => smartForm.size === 'M',
  isSmartFormLarge: ({ smartForm }) => smartForm.size.substr(-1) === 'L',
  isSmartForm: ({ formWidget }) => formWidget === 'smartForm',
  isSmartFormPopin: ({ formWidget, smartForm }) =>
    formWidget === 'smartForm' && smartForm.type === 'popin',
  isSmartFormOpen: ({ formWidget, smartForm }) =>
    formWidget === 'smartForm' && smartForm.isOpen,
  isSmartFormCompactApplied: (
    _state,
    { isSmartFormCompact, isSmartFormSmall }
  ) => isSmartFormCompact && !isSmartFormSmall,
  isSinglePaymentButton: ({ smartForm }, { isSmartFormPopin }) =>
    smartForm.singlePaymentButton && !isSmartFormPopin,
  isCardMethod:
    ({ smartForm: { activeMethod } }) =>
    (method = activeMethod) =>
      method === 'CARDS',
  isSelectedMethod:
    ({ smartForm: { selectedMethod } }) =>
    method =>
      method === selectedMethod,
  isActiveMethod:
    ({ smartForm: { activeMethod } }) =>
    method =>
      method === activeMethod,
  isActiveGroup:
    ({ smartForm: { activeGroup } }) =>
    method =>
      method === activeGroup,
  isGroupActive: ({ smartForm: { activeGroup } }) => activeGroup !== null,
  hasCardBrand: ({ smartForm: { dnaCardBrands } }) => {
    return brand => !!~dnaCardBrands.indexOf(brand)
  },
  hasAnyCardBrand: (_state, { effectiveCardBrandList }) =>
    effectiveCardBrandList.length > 0,
  hasCardMethodOption: (_, getters) =>
    !!~getters.effectivePaymentMethodList?.indexOf('CARDS'),
  hasCardMethodAvailable: ({ smartForm: { dnaPaymentMethods }, dna }) =>
    !!~dnaPaymentMethods?.indexOf('CARDS'),
  hasCardInRoot: (state, getters) =>
    getters.hasCardMethodAvailable && !getters.hasExactSmartButton('CARDS'),
  hasAnyActiveMethod: ({ smartForm }) => !!smartForm.activeMethod,
  isCardMethodActive: ({ smartForm }) => smartForm.activeMethod === 'CARDS',
  isMethodAvailable: (_, getters) => method => {
    return !!~getters.effectivePaymentMethodList.indexOf(method)
  },
  isOnlyMethodAvailable:
    (_, { isMethodAvailable, numOfPaymentMethods }) =>
    method => {
      return isMethodAvailable(method) && numOfPaymentMethods === 1
    },
  isMethodAvailableInDna: state => method => {
    return !!~state.smartForm.dnaPaymentMethods.indexOf(method)
  },
  isMethodWhitelisted:
    ({ _internals }) =>
    method => {
      return !_internals.devMode
        ? paymentMethodsConf.whitelist.includes(method)
        : true
    },
  hasSeveralPaymentMethods: (_state, { numOfPaymentMethods, isWallet }) =>
    numOfPaymentMethods > 1 || (numOfPaymentMethods === 1 && isWallet),
  hasPaymentMethods: (state, { numOfPaymentMethods }) =>
    numOfPaymentMethods > 0,
  numOfPaymentMethods: (_, getters) =>
    getters.effectivePaymentMethodList.length,
  numOfOtherPaymentMethods: (_, getters) =>
    getters.effectivePaymentMethodList.filter(method => method !== 'CARDS')
      .length,
  getRedirectionType:
    ({ dna, smartForm }, getters) =>
    method => {
      // For cards - always embedded
      if (getters.isCardMethod(method)) return 'embedded'
      // First priority - forced type
      if (smartForm.forceRedirectionType) return smartForm.forceRedirectionType
      const configuredType = paymentMethodsConf.type[method]
      // Second priority - configuration
      if (configuredType)
        return typeof configuredType === 'string'
          ? configuredType
          : configuredType[
              getters.isMobile || getters.isTablet ? 'mobile' : 'default'
            ]
      // Third priority - dna
      if (dna?.smartForm?.[method]?.allowIFrame) return 'iframe'
      // Default - popup
      return 'popup'
    },
  isDeadEndPaymentMethod: ({ smartForm }) => smartForm.deadEndPaymentMethod,
  hasDeadEndPaymentMethod:
    ({ dna, smartForm: { activeMethod } }) =>
    (method = activeMethod) =>
      paymentMethodsConf.deadEndPaymentMethod?.includes(method) ||
      dna.smartForm?.[method]?.deadEndPaymentMethod,
  paymentMethod: ({ smartForm }) => {
    return smartForm?.activeMethod // KJS-3606
  },
  paymentMethodMetadata: ({ smartForm }) => smartForm.activeMethodMetadata,
  isGroupingActivated: (
    { smartForm },
    {
      numOfPaymentMethods,
      isSinglePaymentButton,
      activeNestedMethodAndMultipleGridColumns
    }
  ) => {
    let totalNumOfPaymentMethods = numOfPaymentMethods

    // The nested payment method is removed from the effective method list when active (grid 2+ cols)
    // So we need to manually add it to the amount to avoid weird interactions with grouping
    if (activeNestedMethodAndMultipleGridColumns) totalNumOfPaymentMethods++

    return (
      totalNumOfPaymentMethods > smartForm.groupingThreshold &&
      !isSinglePaymentButton
    )
  },
  hasSmartButton:
    ({ smartForm }) =>
    method => {
      return !!smartForm.smartButtons.find(buttonMethod =>
        buttonMethod.includes(method)
      )
    },
  hasExactSmartButton:
    ({ smartForm }) =>
    method => {
      return smartForm.smartButtons?.includes(method)
    },
  isExactSmartButtonActive: ({ smartForm }, { hasExactSmartButton }) =>
    smartForm.activeMethod &&
    hasExactSmartButton(
      MethodDescriptor.fromArray([
        smartForm.activeMethod,
        smartForm.activeMethodMetadata
      ]).toString()
    ),
  isSmartButtonActive: ({ smartForm }, { hasSmartButton }) =>
    hasSmartButton(smartForm.activeMethod),
  // checks if card form must be displayed as a separated extended form
  cardsFormExpanded: ({ smartForm }, getters) => {
    const {
      hasCardMethodOption,
      hasSeveralPaymentMethods,
      isWallet,
      hasDnaInstallments,
      allCardBrandsFiltered,
      compactNestedMethodEdgeCase
    } = getters
    return (
      (!smartForm.displayOptions.cardsIntegrated ||
        (hasCardMethodOption && !hasSeveralPaymentMethods && !isWallet) ||
        (hasCardMethodOption && hasDnaInstallments)) &&
      smartForm.type === 'embedded' &&
      !allCardBrandsFiltered &&
      !compactNestedMethodEdgeCase
    )
  },
  outsideCardsForm: ({ smartForm }) => smartForm.outsideCardsForm,
  hasCardHeader: (
    { smartForm },
    {
      cardsFormExpanded,
      hasAnyCardBrand,
      isWallet,
      hasTokens,
      hasPartialPayments
    }
  ) =>
    !!cardsFormExpanded &&
    !!smartForm.displayOptions.cardHeader &&
    hasAnyCardBrand &&
    !smartForm.outsideCardsForm &&
    (!isWallet || !hasTokens) &&
    !hasPartialPayments,

  groupedPaymentMethods: (_, getters) => {
    const grouped = []
    for (const group in getters.effectivePaymentMethodGroupList) {
      if (group !== 'single')
        grouped.push(...getters.effectivePaymentMethodGroupList[group])
    }
    return grouped
  },
  activeGroupPaymentMethods: (state, getters) => {
    return (methodGroupList = getters.effectivePaymentMethodGroupList) => {
      const { smartForm } = state
      return methodGroupList[smartForm.activeGroup] || []
    }
  },
  getPaymentMethodLabel: (_, getters) => method => {
    if (!method) return method
    method = getters.getShortMethodKey(method)

    const key = `smartform_method_${method.toLowerCase()}`
    const translation = getters.translate(key)
    if (translation !== key) return translation
    return paymentMethodsConf.labels[method] || method
  },
  /**
   * In alignment with getPaymentMethodLabel getters, return a group label given
   * its key.
   *
   * @returns {string}
   * @since KJS-4813
   */
  getGroupLabel: (_, getters) => groupKey => {
    if (!groupKey) return groupKey

    const key = `smartform_group_${groupKey.toLowerCase()}`
    const translation = getters.translate(key)
    if (translation !== key) return translation
    return paymentMethodsConf.labels[groupKey] || groupKey
  },
  getCustomPaymentMethodLabel:
    ({ smartForm, language }) =>
    paymentMethod => {
      return smartForm.paymentMethods?.[paymentMethod]?.label[language] || null
    },
  getCustomPaymentMethodIcon:
    ({ smartForm }) =>
    async paymentMethod => {
      const icon = smartForm.paymentMethods?.[paymentMethod]?.icon
      if (!icon) return null
      return isValidUrl(icon) ? await urlToSvg(icon) : ensureUniqId(icon)
    },
  isFallbackSmartButtonVisible: (_, getters) => {
    const {
      numOfPaymentMethods,
      isWallet,
      isSmartFormPopin,
      isSmartFormForcedToken,
      effectivePaymentMethodList,
      hasPartialPayments,
      activeNestedMethodAndMultipleGridColumns
    } = getters

    const isCardsAvailable = effectivePaymentMethodList?.includes('CARDS')
    return (
      !isSmartFormPopin &&
      numOfPaymentMethods === 1 &&
      !isSmartFormForcedToken &&
      !isCardsAvailable &&
      !isWallet &&
      !hasPartialPayments &&
      !activeNestedMethodAndMultipleGridColumns
    )
  },
  onlySmartButtons: (
    _state,
    { getFormElement, getSmartFormElement, getSmartButtonElements }
  ) => {
    return (
      !getSmartFormElement() &&
      !getFormElement() &&
      getSmartButtonElements().length > 0
    )
  },
  isPopupRedirectionMethodActive: (
    state,
    { getRedirectionType, paymentMethod }
  ) => {
    return (
      !!paymentMethod &&
      ['popup', 'tab'].includes(getRedirectionType(paymentMethod))
    )
  },
  showOverlay: (
    state,
    {
      isSmartFormOpen,
      isApplePayActive,
      isExtrasFormVisible,
      activeNestedMethod,
      isExpandedCardFormActive
    }
  ) => {
    const forcedOverlay = state.form.smartform.overlay.forcedDisplayState
    const { postPaymentRedirecting, redirectPopupOpen, processingPayment } =
      state
    const { activeMethod } = state.smartForm

    // Forced overlay status by the merchant will override any of our internal logic
    if (forcedOverlay !== null) return forcedOverlay

    return (
      isSmartFormOpen ||
      isExtrasFormVisible ||
      postPaymentRedirecting ||
      redirectPopupOpen ||
      processingPayment ||
      (!!activeMethod &&
        !isApplePayActive &&
        !activeNestedMethod &&
        !isExpandedCardFormActive)
    )
  },
  showOverlayMessage: (state, { isCardMethodActive, isApplePayActive }) => {
    const forcedOverlay = state.form.smartform.overlay.forcedDisplayState
    const { postPaymentRedirecting, redirectPopupOpen, processingPayment } =
      state
    const { activeMethod } = state.smartForm

    // If forced by the merchant always display the message
    if (forcedOverlay !== null) return true

    return (
      postPaymentRedirecting ||
      redirectPopupOpen ||
      processingPayment ||
      (!!activeMethod && !isCardMethodActive && !isApplePayActive)
    )
  },
  isSmartFormForcedToken: state =>
    isSmartFormForcedTokenHelper(state?.dna?.tokens),
  smartFormPaymentToken: state => state.dna.tokens?.smartForm?.[0],
  hasNavigatedFrom: state => view => {
    const { history } = state.smartForm.navigation
    return history[history.length - 2] === view
  },
  /**
   * When userPaymentMethodsOrder is set, return sorted copy of
   * effectivePaymentMethodList.
   *
   * Any methods missing in userPaymentMethodsOrder is placed at the end but
   * maintain their relative order with other unspecified methods.
   *
   * If CARDS or any unavailable method is specified, it is ignored and a
   * warning is displayed.
   *
   * Illicado is always placed at the end of the array to avoid iframe rendering issues
   *
   * @param {Object} state
   * @returns {string[]}
   * @since KJS-3552
   */
  sortedPaymentMethods: (state, getters) => {
    const {
      smartForm: { userPaymentMethodsOrder: order }
    } = state
    const { effectiveNonPreferredMethods: pm } = getters

    if (!order?.length) {
      return pm
    }

    const ignoredKeys = order.filter(
      key => !pm.includes(key) || key === 'CARDS'
    )

    if (ignoredKeys.length) {
      const list = ignoredKeys.join(', ')
      console.warn(`KR: the following payment methods will be ignored: ${list}`)
    }

    const sorted = pm.slice().sort((a, b) => {
      if (a === 'CARDS') return -1
      if (b === 'CARDS') return 1
      const idxA = order.includes(a) ? order.indexOf(a) : Infinity
      const idxB = order.includes(b) ? order.indexOf(b) : Infinity
      return idxA - idxB
    })

    return sorted
  },
  isNavigationOnContent: ({ smartForm }) => {
    return smartForm.navigation.currentView === 'content'
  },
  allCardBrandsFiltered: (
    { smartForm: { smartButtons } },
    { hasAnyCardBrand }
  ) => {
    return !hasAnyCardBrand || smartButtons.includes('CARDS')
  },
  isPaymentDone: ({ smartForm }) => smartForm.paymentDone,
  popinOpensMethodDirectly: (
    {},
    { numOfPaymentMethods, isWalletSmartForm, hasTokens, isSmartFormPopin }
  ) =>
    isSmartFormPopin &&
    numOfPaymentMethods === 1 &&
    !(isWalletSmartForm && hasTokens),
  hasCardFormExpandedInside: (
    _state,
    { cardsFormExpanded, outsideCardsForm }
  ) => {
    return cardsFormExpanded && !outsideCardsForm
  },
  renderGenericEmbeddedHeader: (
    _state,
    {
      hasSeveralPaymentMethods,
      isWallet,
      cardsFormExpanded,
      hasCardMethodAvailable,
      outsideCardsForm,
      hasPartialPayments,
      isRegisterMode,
      compactNestedMethodEdgeCase
    }
  ) => {
    return (
      (!isRegisterMode &&
        hasSeveralPaymentMethods &&
        (!cardsFormExpanded ||
          (cardsFormExpanded && isWallet) ||
          !hasCardMethodAvailable ||
          outsideCardsForm)) ||
      hasPartialPayments ||
      compactNestedMethodEdgeCase
    )
  },
  renderEmbeddedForm(
    {},
    {
      outsideCardsForm,
      cardsFormExpanded,
      hasAnyCardBrand,
      isSmartFormForcedToken,
      hasCardMethodOption
    }
  ) {
    return (
      !outsideCardsForm &&
      cardsFormExpanded &&
      hasAnyCardBrand &&
      hasCardMethodOption &&
      !isSmartFormForcedToken
    )
  },
  hasOtherPaymentMethods: (_, { effectiveNonPreferredMethods }) => {
    return effectiveNonPreferredMethods.filter(pm => pm !== 'CARDS').length > 0
  },
  isWalletSmartFormOrRegister: (
    _state,
    { isWalletSmartForm, hasTokens, isRegisterMode }
  ) => {
    return (isWalletSmartForm && hasTokens) || isRegisterMode
  },
  canRegisterMethod: ({ dna }) => {
    return method => {
      const mids = dna?.smartForm?.[method]?.metadata?.mids ?? null

      if (!mids) return false

      return mids.every(mid => mid.paymentByToken === true)
    }
  },
  showOpmLabel: (
    _state,
    {
      isWalletSmartFormOrRegister,
      hasOtherPaymentMethods,
      hasCardFormExpandedInside,
      hasPartialPayments,
      numOfPaymentMethods,
      hasPreferredPaymentMethods
    }
  ) => {
    if (hasPartialPayments && numOfPaymentMethods === 1) return false
    return (
      (isWalletSmartFormOrRegister ||
        hasCardFormExpandedInside ||
        hasPartialPayments ||
        hasPreferredPaymentMethods) &&
      hasOtherPaymentMethods
    )
  },
  isCompactWallet: ({
    smartForm: {
      layout,
      wallet: { layout: walletLayout }
    }
  }) => {
    return (
      walletLayout === 'compact' ||
      (walletLayout === 'inherit' && ['compact', 'grid'].includes(layout))
    )
  },
  /**
   * Confirm based on context & config whether the group name should be added
   * as a suffix.
   *
   * @returns {(groupKey: string) => boolean}
   * @since KJS-4813
   */
  shouldAddGroupSuffix: (_state, getters) => {
    return groupKey => {
      if (getters.isGroupingActivated) return false
      const conf = paymentMethodsConf.groups[groupKey]

      return !!conf && conf.suffixWhenUngrouped === true
    }
  },
  /**
   * For a given payment method key, return the parent group key in
   * the configuration or null if none is found.
   *
   * @returns {(groupKey: string) => string | null}
   * @since KJS-4813
   */
  getParentGroup: () => {
    return memoize(method => {
      for (const groupKey in paymentMethodsConf.groups) {
        const conf = paymentMethodsConf.groups[groupKey]

        if (conf.methods.includes(method)) {
          return groupKey
        }
      }
      return null
    })
  },
  requirePartialPayment() {
    return method =>
      paymentMethodsConf.notAllowedWithoutPartialPayment.includes(method)
  },
  effectiveCardBrandList(state, getters) {
    const {
      dna,
      smartForm: {
        smartButtons,
        excludedPaymentMethods = [],
        allowedPaymentMethods
      }
    } = state
    const { isSmartForm, cardBrandAsMethodRawList, isCardMethod } = getters
    if (dna?.cards == null) return []

    let brands = Object.keys(dna.cards).filter(
      brand => brand !== 'DEFAULT' && !cardBrandAsMethodRawList.includes(brand)
    )

    // Filter the brands of smartButtons (including the sub-brands and co-brands)
    const smartButtonBrands = smartButtons.reduce((acc, curr) => {
      const method = MethodDescriptor.create(curr)
      // not adding cardBrandsAsMethod already filtered above
      if (
        !isCardMethod(method.name) ||
        !method.metadata ||
        cardBrandAsMethodRawList.includes(method.metadata)
      )
        return acc
      return acc.concat(method.metadata.split(','))
    }, [])
    brands = brands.filter(brand => {
      return (
        !smartButtonBrands.includes(brand) &&
        !smartButtonBrands.includes(CoBrands.brands[brand]) &&
        !smartButtonBrands.includes(CoBrands.onlyOneBrand[brand]) &&
        !smartButtonBrands.includes(SubBrands[brand])
      )
    })

    // Exclude legacy kr-embedded
    if (!isSmartForm) {
      brands = brands.filter(brand => !notAllowedInKrEmbedded?.includes(brand))
    }

    // Filter allowed payment methods (card brands)
    if (!allowedPaymentMethods.includes('all')) {
      const upperAllowedMethods = allowedPaymentMethods.map(m =>
        m.toUpperCase()
      )
      // If CARDS is not in allowedPaymentMethods, only keep brands that are explicitly allowed
      if (!upperAllowedMethods.includes('CARDS')) {
        brands = brands.filter(brand => upperAllowedMethods.includes(brand))
      }
    }

    // Filter excluded payment methods (card brands)
    if (excludedPaymentMethods.length > 0) {
      const upperExcludedMethods = excludedPaymentMethods.map(m =>
        m.toUpperCase()
      )
      brands = brands.filter(brand => !upperExcludedMethods.includes(brand))
    }

    return brands
  },
  cardBrandsWithoutCardBrandsAsMethod(state, getters) {
    const { dna } = state
    const { cardBrandAsMethodList } = getters
    if (dna?.cards == null) return []

    return Object.keys(dna.cards).filter(
      brand => brand !== 'DEFAULT' && !cardBrandAsMethodList.includes(brand)
    )
  },
  /**
   * Use a getters instead of state.availablePaymentMethod as much as possible.
   *
   * @since KJS-4367
   * @see KJS-4367 | Add SplitPayment filter
   * @see KJS-4898 | Add NotAllowedWithoutPartialPayments filter
   */
  effectivePaymentMethodList: (state, getters) => {
    const {
      allowedPaymentMethods,
      excludedPaymentMethods,
      availablePaymentMethods
    } = state.smartForm
    const { dna } = state
    const {
      requirePartialPayment,
      hasInstallmentSelected,
      filterMethodWithInstallments,
      partialPaymentsAllowed,
      cardBrandAsMethodList,
      effectiveCardBrandList,
      hasExactSmartButton,
      activeNestedMethod,
      isActiveNestedAndCompactOrGridColumns
    } = getters

    let effectiveList = availablePaymentMethods.slice(0)

    // Filter 1 - Allowed payment methods
    if (!allowedPaymentMethods.includes('all')) {
      const upperAllowedMethods = allowedPaymentMethods.map(m =>
        m.toUpperCase()
      )
      effectiveList = effectiveList.filter(method =>
        upperAllowedMethods.includes(method.toUpperCase())
      )
    }

    // Filter 2 - Excluded payment methods (including card brands)
    if (excludedPaymentMethods.length > 0) {
      const upperExcludedMethods = excludedPaymentMethods.map(m =>
        m.toUpperCase()
      )
      effectiveList = effectiveList.filter(method => {
        const upperMethod = method.toUpperCase()
        return !upperExcludedMethods.includes(upperMethod)
      })
    }

    // Filter 3 - SplitPayment - Filter out incompatible methods upon selection
    if (hasInstallmentSelected) {
      effectiveList = effectiveList.filter(method =>
        filterMethodWithInstallments(method)
      )
    }

    // Filter 4 - filter payment methods not allowed without partialPayments
    if (!partialPaymentsAllowed) {
      effectiveList = effectiveList.filter(
        method => !requirePartialPayment(method)
      )
    }

    // Filter 5 - Card brands handled as a payment method
    for (const brand of cardBrandAsMethodList) {
      if (partialPaymentsAllowed || !requirePartialPayment(brand)) {
        const brandConfigIndex = effectiveList.indexOf(brand)
        if (brandConfigIndex !== -1) {
          effectiveList[brandConfigIndex] = `CARDS:${brand}`
        } else {
          effectiveList.push(`CARDS:${brand}`)
        }
      }
    }

    // Filter 6 - Remove CARDS if no brand remain (e.g: if ILLICADO is the only brand available)
    if (
      effectiveCardBrandList.length === 0 &&
      effectiveList.includes('CARDS')
    ) {
      effectiveList.splice(effectiveList.indexOf('CARDS'), 1)
    }

    // Filter 7 - SmartButton are extracted from default SmartForm methods
    effectiveList = effectiveList.filter(method => !hasExactSmartButton(method))

    // Filter 8 - Hide the selected nested (ILLICADO) method when active in grid with multiple columns
    // or in compact mode
    if (isActiveNestedAndCompactOrGridColumns) {
      effectiveList = effectiveList.filter(
        method => method !== activeNestedMethod.toString()
      )
    }
    // Filter 9 - Remove other payment methods if token is forced
    if (dna?.tokens?.forced) {
      const smartFormTokenType = dna?.tokens?.smartForm?.[0]?.paymentMethodType
      const paymentMethodToPreserve = smartFormTokenType?.startsWith('PAYPAL')
        ? smartFormTokenType
        : 'CARDS'
      return [paymentMethodToPreserve]
    }

    return effectiveList
  },
  preferredMethodsSource: state => {
    const { preferredPaymentMethods, dnaPreferredPaymentMethods } =
      state.smartForm

    // User-defined preferred methods override DNA-provided ones when present
    return preferredPaymentMethods ?? dnaPreferredPaymentMethods
  },
  effectivePreferredMethods: (_state, getters) => {
    const {
      isSmartFormPopin,
      preferredMethodsSource,
      getLongMethodKey,
      effectivePaymentMethodList,
      isCardMethod
    } = getters

    if (isSmartFormPopin) return []

    // "ILLICADO" → "CARDS:ILLICADO"
    const preferredMethodKeys = preferredMethodsSource.map(getLongMethodKey)

    return [...new Set(preferredMethodKeys)].filter(
      method =>
        effectivePaymentMethodList.includes(method) && !isCardMethod(method)
    )
  },
  hasPreferredPaymentMethods: (_state, getters) =>
    getters.effectivePreferredMethods.length > 0,
  effectiveNonPreferredMethods: (_state, getters) => {
    const {
      hasPreferredPaymentMethods,
      effectivePaymentMethodList,
      effectivePreferredMethods
    } = getters

    if (!hasPreferredPaymentMethods) return effectivePaymentMethodList

    return effectivePaymentMethodList.filter(
      method => !effectivePreferredMethods.includes(method)
    )
  },
  /**
   * Use a getters instead of state.availablePaymentMethod as much as possible.
   *
   * @since KJS-4368
   */
  paymentMethodsGroupList: (state, getters) => {
    return methodsList => {
      const groups = { single: [] }

      let paymentMethods = methodsList.slice(0)

      // Grouped methods

      if (getters.isGroupingActivated) {
        Object.entries(paymentMethodsConf.groups).forEach(([name, config]) => {
          const groupedMethods = paymentMethods.filter(method =>
            config.methods.includes(method)
          )
          if (groupedMethods.length > 1) {
            // Only add a group if there is more than one entry, with a single entry we add the method independently
            paymentMethods = paymentMethods.filter(
              method => !groupedMethods.includes(method)
            )
            groups[name] = groupedMethods
          } else {
            // Need to reset the groups to an empty array in order to properly remove a group if it has been removed from the DNA
            // See KJS-4259
            groups[name] = []
          }
        })
      }

      // Ungrouped methods
      for (const method of paymentMethods) {
        // Cards method
        if (
          !state.smartForm.displayOptions.cardsIntegrated &&
          method === 'CARDS'
        )
          groups.single.unshift('CARDS')
        else {
          groups.single.push(method)
        }
      }

      // Order the groups
      const groupOrder = [...Object.keys(paymentMethodsConf.groups)]
      const orderedGroups = Object.keys(groups)
        .sort((a, b) => {
          return groupOrder.indexOf(a) - groupOrder.indexOf(b)
        })
        .reduce((obj, key) => {
          obj[key] = groups[key]
          return obj
        }, {})

      return orderedGroups
    }
  },
  /**
   * Group list of all payment methods
   */
  effectivePaymentMethodGroupList: (_, getters) =>
    getters.paymentMethodsGroupList(getters.effectivePaymentMethodList),
  /**
   * Group list of non-preferred payment methods
   * (when no preferred methods are present, non-preferred pm === all pm)
   */
  effectiveNonPreferredPMGroupList: (_, getters) =>
    getters.paymentMethodsGroupList(getters.sortedPaymentMethods),
  /**
   * Group list of preferred payment methods
   */
  effectivePreferredPMGroupList: (_state, getters) =>
    getters.paymentMethodsGroupList(getters.effectivePreferredMethods),
  isActiveNestedPreferredPM: (_state, getters) => {
    const activeNestedMethod = getters.activeNestedMethod
    if (!activeNestedMethod) return false

    const normalizePMKey = getters.getLongMethodKey
    return !!getters.preferredMethodsSource.some(
      method => normalizePMKey(method) === normalizePMKey(activeNestedMethod)
    )
  },
  effectiveNativeLookMethods: ({ smartForm, smartButton }, getters) => {
    const { enabled, paymentMethods: smartFormMethods } = smartForm.nativeLook
    const { paymentMethods: smartBtnMethods } = smartButton.nativeLook
    const { nativeLookMethods: supportedMethods } = paymentMethodsConf

    const filterConfiguredMethods = ({ methods, isSmartBtn }) =>
      methods.filter(
        method =>
          getters.hasExactSmartButton(method) === isSmartBtn &&
          supportedMethods.includes(method)
      )

    const resolvedConfiguredMethods = [
      ...filterConfiguredMethods({
        methods: smartFormMethods.length ? smartFormMethods : supportedMethods,
        isSmartBtn: false
      }),
      ...filterConfiguredMethods({ methods: smartBtnMethods, isSmartBtn: true })
    ]

    const getCandidateMethods = ({
      methodsList,
      isPPMList,
      nativeLookEnabled
    }) => {
      if (!nativeLookEnabled) return []

      // In grid 2+ columns and compact layout modes, the active nested method is removed from the PMs lists.
      // Restore it here in the appropriate list so native look eligibility remains correct.
      const shouldIncludeActiveNestedMethod =
        getters.isActiveNestedAndCompactOrGridColumns &&
        getters.isActiveNestedPreferredPM === isPPMList

      return shouldIncludeActiveNestedMethod
        ? [...methodsList, getters.activeNestedMethod.toString()]
        : methodsList
    }

    const candidateMethods = [
      ...getCandidateMethods({
        methodsList: getters.effectivePreferredMethods,
        isPPMList: true,
        nativeLookEnabled: enabled.preferredMethods
      }),
      ...getCandidateMethods({
        methodsList: getters.effectiveNonPreferredMethods,
        isPPMList: false,
        nativeLookEnabled: enabled.otherMethods
      }),
      ...smartForm.smartButtons
    ]

    return resolvedConfiguredMethods.filter(method =>
      candidateMethods.includes(method)
    )
  },
  isEffectiveNativeLookMethod: (_state, getters) => {
    return method => getters.effectiveNativeLookMethods.includes(method)
  },
  /**
   * Used by openMethod action
   * @since KJS-4934
   */
  shouldOpenCardFormPopin(_state, getters) {
    const {
      isCardMethod,
      hasCardToken,
      cardsFormExpanded,
      hasSmartButton,
      isCardBrandAsMethod,
      cardFormInsideButton
    } = getters

    return method => {
      method = MethodDescriptor.create(method)
      return (
        (isCardMethod(method.name) &&
          (hasCardToken(method.metadata) ||
            !cardsFormExpanded ||
            hasSmartButton(method.name))) ||
        (isCardBrandAsMethod(method.name) && !cardFormInsideButton(method.name))
      )
    }
  },
  requireModalCardForm(state, getters) {
    const {
      isSmartForm,
      isFormPopin,
      cardsFormExpanded,
      hasCardTokens,
      isWallet,
      isCardBrandAsMethod
    } = getters

    const {
      smartForm: { smartButtons }
    } = state

    const hasACardSmartButton = !!smartButtons.find(
      m => m.includes('CARDS') && !isCardBrandAsMethod(m)
    )

    return (
      isSmartForm &&
      (isFormPopin || cardsFormExpanded) &&
      (hasACardSmartButton || (hasCardTokens && isWallet))
    )
  },
  requireAdditionalForms(_state, getters) {
    return getters.hasCardFormInsideButton || getters.requireModalCardForm
  },
  /**
   * E.g:
   * CARDS => CARDS
   * CARDS:VISA => CARDS
   * CARDS:<token> => CARDS
   * CARDS:ILLICADO => ILLICADO
   * CARDS:OG_<BRAND> => OG_<BRAND>
   * PSE => PSE
   * ILLICADO => ILLICADO
   * OG_<BRAND> => OG_<BRAND>
   */
  getShortMethodKey(_state, getters) {
    return method => {
      try {
        method = MethodDescriptor.create(method)
      } catch (e) {
        return method
      }
      return getters.isCardBrandAsMethod(method) && method.metadata
        ? method.metadata
        : method.name
    }
  },
  /**
   * E.g:
   * ILLICADO => CARDS:ILLICADO
   * OG_<BRAND> => CARDS:OG_<BRAND>
   * CARDS => CARDS
   * PSE => PSE
   */
  getLongMethodKey(_state, getters) {
    return method => {
      let methodDescriptor
      try {
        methodDescriptor = MethodDescriptor.create(method)
      } catch {
        return method
      }

      const { name, metadata } = methodDescriptor

      if (getters.isCardBrandAsMethod(methodDescriptor))
        return metadata ? methodDescriptor.toString() : `CARDS:${name}`

      return name
    }
  },
  isCardBrandAsMethod(_state, getters) {
    const { cardBrandAsMethodList } = getters

    return method => {
      if (!method) return false
      method = MethodDescriptor.create(method)
      let brand = method.name
      if (method.metadata && method.name === 'CARDS') {
        brand = method.metadata
      }
      return cardBrandAsMethodList.includes(brand)
    }
  },
  isCardBrandAsMethodForm(_state, { relatedForm, isCardBrandAsMethod }) {
    return formId => {
      return isCardBrandAsMethod(relatedForm(formId))
    }
  },
  // Checks if the expanded card form is available and selected at the moment
  isExpandedCardFormActive(state, { cardsFormExpanded }) {
    const { activeMethod, activeMethodMetadata } = state.smartForm
    return (
      activeMethod === 'CARDS' && !activeMethodMetadata && cardsFormExpanded
    )
  },
  isFormLocked(state, { hasAnyActiveMethod, isAnyNestedMethodActive }) {
    const { paymentDone } = state.smartForm
    return paymentDone || (hasAnyActiveMethod && !isAnyNestedMethodActive)
  },
  defaultActiveMethod(
    _state,
    { cardsFormExpanded, isGridMode, isGroupActive }
  ) {
    // Without the expanded form the default is always null
    if (!cardsFormExpanded) return null

    // With card form expanded:
    // If no group is selected always CARDS
    // If a group is selected only return CARDS on grid mode (cards are not visible in other cases)
    return !isGroupActive || (isGroupActive && isGridMode) ? 'CARDS' : null
  },
  isDefaultActiveMethod(
    { smartForm: { activeMethod, activeMethodMetadata } },
    { defaultActiveMethod }
  ) {
    return activeMethod === defaultActiveMethod && !activeMethodMetadata
  },
  isSandboxMail({ dna }) {
    return !!dna?.customerEmail?.includes('@sandbox')
  },
  isAnySimulatorOpen(
    _state,
    {
      isGooglePaySimulatorOpen,
      isApplePaySimulatorOpen,
      isSamsungPaySimulatorOpen
    }
  ) {
    return (
      isGooglePaySimulatorOpen ||
      isApplePaySimulatorOpen ||
      isSamsungPaySimulatorOpen
    )
  },
  activeCardFormMethod(state, { cardBrandAsMethodList }) {
    const methods = cardBrandAsMethodList
    if (methods.includes(state.activeForm)) return state.activeForm
    if (methods.includes(state.smartForm.activeMethodMetadata))
      return state.smartForm.activeMethodMetadata
    return null
  }
}
